import { Pencil, Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useClients, useCurrentUser, useDeleteClient, useOrganizations } from "@/api/hooks";
import type { ClientResponse, OrganizationSummary } from "@/api/types";
import { ClientForm } from "@/components/ClientForm";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { shortId } from "@/lib/format";

/** Compact one-line address rendering for the table. Returns "—"
 * when the client has no address fields at all. */
function shortAddress(client: ClientResponse): string {
  const cityRegion = [client.city, client.region].filter(Boolean).join(", ");
  const parts = [client.address_line1, cityRegion].filter(Boolean);
  return parts.length === 0 ? "—" : parts.join(" · ");
}

/** Per-organization homeowner directory.
 *
 * Org users (admin + member) both manage. PencilOut staff get a
 * cross-org view + filter dropdown. */
export function ClientsPage() {
  const me = useCurrentUser();
  const isPencilOutAdmin = me.data?.role === "pencilout_admin";

  const [orgFilter, setOrgFilter] = useState("");
  const clientsQuery = useClients(isPencilOutAdmin && orgFilter ? orgFilter : null);
  const orgsQuery = useOrganizations();
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-mono text-2xl font-bold tracking-tight">Clients</h1>
          <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
            Homeowners you're quoting. Each job optionally links to one client — their name +
            contact info show up on the quote PDF (eventually) and their address goes on every line
            item review screen.
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> New client
          </button>
        )}
      </div>

      {creating && (
        <ClientForm
          mode="create"
          isPencilOutAdmin={isPencilOutAdmin}
          orgs={orgsQuery.data ?? []}
          onClose={() => setCreating(false)}
        />
      )}

      {isPencilOutAdmin && orgsQuery.data && orgsQuery.data.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="label-mono">Filter by org:</span>
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            className="rounded border border-rule bg-paper-warm px-2 py-1 text-sm text-graphite focus:border-graphite focus:outline-none"
          >
            <option value="">All organizations</option>
            {orgsQuery.data.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {clientsQuery.isLoading && <p className="text-sm text-graphite-soft">Loading clients…</p>}

      {clientsQuery.error && !clientsQuery.data && (
        <p className="text-sm text-red-pencil">
          {clientsQuery.error instanceof ApiError
            ? clientsQuery.error.detail
            : "Could not load clients."}
        </p>
      )}

      {clientsQuery.data && clientsQuery.data.length === 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm p-12 text-center">
          <Users className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-4 text-graphite-soft">
            No clients yet. Add one to start associating jobs and quotes with the homeowner being
            billed.
          </p>
        </div>
      )}

      {clientsQuery.data && clientsQuery.data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rule bg-paper-warm">
          <table className="w-full">
            <thead className="border-b border-rule bg-paper">
              <tr>
                <th className="label-mono px-4 py-2 text-left">Name</th>
                <th className="label-mono px-4 py-2 text-left">Address</th>
                <th className="label-mono px-4 py-2 text-left">Contact</th>
                {isPencilOutAdmin && <th className="label-mono px-4 py-2 text-left">Org</th>}
                <th className="label-mono px-4 py-2 text-right">Jobs</th>
                <th className="label-mono px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clientsQuery.data.map((client) => (
                <ClientRow
                  key={client.id}
                  client={client}
                  isPencilOutAdmin={isPencilOutAdmin}
                  orgs={orgsQuery.data ?? []}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ClientRow({
  client,
  isPencilOutAdmin,
  orgs,
}: {
  client: ClientResponse;
  isPencilOutAdmin: boolean;
  orgs: OrganizationSummary[];
}) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteClient = useDeleteClient();

  if (editing) {
    return (
      <tr>
        <td colSpan={isPencilOutAdmin ? 6 : 5} className="px-4 py-3">
          <ClientForm
            mode="edit"
            client={client}
            isPencilOutAdmin={isPencilOutAdmin}
            orgs={orgs}
            onClose={() => setEditing(false)}
          />
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-rule last:border-0 hover:bg-pencil-yellow-soft">
      <td className="px-4 py-3 align-top">
        <Link
          to={`/clients/${client.id}`}
          className="font-medium text-graphite hover:text-ink hover:underline"
        >
          {client.name}
        </Link>
        <div className="mt-1 font-mono text-[10px] text-graphite-faint">{shortId(client.id)}</div>
      </td>
      <td className="px-4 py-3 align-top text-sm text-graphite-soft">{shortAddress(client)}</td>
      <td className="px-4 py-3 align-top font-mono text-xs text-graphite-soft">
        {client.email ?? "—"}
        {client.phone && <div className="mt-1">{client.phone}</div>}
      </td>
      {isPencilOutAdmin && (
        <td className="px-4 py-3 align-top text-graphite-soft">
          {client.organization_name ?? "—"}
        </td>
      )}
      <td className="px-4 py-3 text-right align-top font-mono tabular-nums">{client.job_count}</td>
      <td className="px-4 py-3 text-right align-top">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => setEditing(true)}
            title="Edit client"
            className="rounded p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            disabled={deleteClient.isPending}
            title="Delete client"
            className="rounded p-1.5 text-graphite-soft transition hover:bg-red-pencil/10 hover:text-red-pencil disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
        <span className="mt-1 block font-mono text-[10px] text-graphite-faint">
          {shortId(client.id)}
        </span>

        {confirmingDelete && (
          <ConfirmDialog
            title={`Delete ${client.name}?`}
            body={
              <>
                Removes this client. Jobs already linked to them stay; their{" "}
                <span className="font-mono">client_id</span> field clears to NULL. Quotes already
                drafted are unaffected.
                {deleteClient.error && (
                  <span className="mt-2 block text-red-pencil">
                    {deleteClient.error instanceof ApiError
                      ? deleteClient.error.detail
                      : "Could not delete."}
                  </span>
                )}
              </>
            }
            confirmLabel="Delete client"
            variant="danger"
            busy={deleteClient.isPending}
            onConfirm={() =>
              deleteClient.mutate(client.id, {
                onSettled: () => setConfirmingDelete(false),
              })
            }
            onCancel={() => {
              setConfirmingDelete(false);
              deleteClient.reset();
            }}
          />
        )}
      </td>
    </tr>
  );
}
